/**
 * Per diem for a trip (ADR 0030, plan §5.7, EC-5, EC-39). Pure and RN-safe; money is decimal.js
 * in, decimal strings out (CLAUDE.md invariant 2).
 *
 * One line per trip day, so the traveller, the approver and the settlement all see the same
 * arithmetic:
 *
 *  - RATE — the city tier of where that day's NIGHT is spent (the stay covering the night that
 *    starts on the day). The last day has no night of its own and takes the previous night's city;
 *    a day with neither (a day trip) takes `dayTripCityTier`, else `tier_3` (D26).
 *  - FIRST / LAST DAY — paid at `firstDayPct` / `lastDayPct`; a one-day trip at the lower of the two.
 *  - HALF DAY — a first or last day with fewer than `halfDayBelowHours` hours away (from the
 *    departure time to midnight; from midnight to the return time; departure to return on a
 *    one-day trip) is paid at 50%, or the first/last-day percentage if that is lower. Without
 *    times the rule cannot apply.
 *  - MEALS — each meal provided (by the hotel, the client…) removes its percentage of the city's
 *    full daily rate, never taking the day below zero.
 *  - LODGING IN LIEU — a night spent in a stay marked `lodgingInLieu` (with friends or family, no
 *    hotel) adds that tier's lodging-in-lieu amount to the day the night starts.
 *  - PERSONAL DAYS — no per diem and no lodging in lieu (EC-5); listed in `excludedDates`.
 *
 * Each line is rounded half-up to `MONEY_SCALE` places and the totals are sums of the rounded lines,
 * so the lines always add up to the total shown.
 */

import { Decimal } from 'decimal.js';
import type { TravelCityTier } from '../enums.js';
import { ValidationError } from '../errors.js';
import { addDays, clockMinutes, diffDays, eachDay } from './dates.js';

/**
 * Longest trip, in days, the travel schemas accept and per diem will compute. One line is built per
 * day, so an unbounded range is a way to stall the API; a year covers any real assignment.
 */
export const MAX_TRAVEL_TRIP_DAYS = 366;

/** Decimal places of a per-diem line. Domestic v1 is INR-style two-decimal currencies. */
export const MONEY_SCALE = 2;

/** `TravelPerDiemRule`, as decimal strings. Percentages are 0–100. */
export interface PerDiemRule {
  /** Null: no half days. */
  halfDayBelowHours: string | null;
  firstDayPct: string;
  lastDayPct: string;
  breakfastPct: string;
  lunchPct: string;
  dinnerPct: string;
}

/** An amount per city tier; a tier with no amount pays nothing (and the line says so). */
export type PerDiemRates = Readonly<Partial<Record<TravelCityTier, string | null>>>;

export interface PerDiemStay {
  /** First night. */
  checkIn: string;
  /** The morning of leaving; the night of `checkOut` is not in this stay. */
  checkOut: string;
  cityTier: TravelCityTier;
  lodgingInLieu?: boolean;
}

export interface PerDiemMealsProvided {
  date: string;
  breakfast?: boolean;
  lunch?: boolean;
  dinner?: boolean;
}

export interface PerDiemInput {
  startDate: string;
  endDate: string;
  /** Local `HH:MM` the traveller leaves on the first day. */
  departureTime?: string | null;
  /** Local `HH:MM` the traveller is back on the last day. */
  returnTime?: string | null;
  stays: readonly PerDiemStay[];
  /** Tier for a day with no night anywhere near it — a day trip's destination. */
  dayTripCityTier?: TravelCityTier | null;
  personalDays?: readonly string[];
  mealsProvided?: readonly PerDiemMealsProvided[];
  rule: PerDiemRule;
  perDiemRates: PerDiemRates;
  lodgingInLieuRates?: PerDiemRates;
  currency: string;
}

export type PerDiemDayKind = 'single' | 'first' | 'middle' | 'last';

export interface PerDiemDayLine {
  date: string;
  cityTier: TravelCityTier;
  dayKind: PerDiemDayKind;
  halfDay: boolean;
  /** Percentage of the rate paid, before meals. */
  pct: string;
  /** The tier's full daily rate. */
  rate: string;
  /** True when the policy has no per-diem rate for this tier. */
  rateMissing: boolean;
  /** `rate × pct`. */
  allowance: string;
  mealDeduction: string;
  lodgingInLieu: string;
  /** `allowance − mealDeduction + lodgingInLieu`. */
  amount: string;
}

export interface PerDiemResult {
  currency: string;
  days: PerDiemDayLine[];
  /** Personal days: no line, nothing paid. */
  excludedDates: string[];
  allowanceTotal: string;
  mealDeductionTotal: string;
  lodgingInLieuTotal: string;
  total: string;
}

const HUNDRED = new Decimal(100);
const HALF_DAY_PCT = new Decimal(50);
const MINUTES_PER_DAY = 1440;

function money(value: Decimal): Decimal {
  return value.toDecimalPlaces(MONEY_SCALE, Decimal.ROUND_HALF_UP);
}

function stayForNight(stays: readonly PerDiemStay[], night: string): PerDiemStay | null {
  return stays.find((s) => s.checkIn <= night && night < s.checkOut) ?? null;
}

/** Hours away on a boundary day, or null when the times needed are missing. */
function hoursAway(kind: PerDiemDayKind, departure: number | null, ret: number | null): Decimal | null {
  let minutes: number | null = null;
  if (kind === 'first' && departure !== null) minutes = MINUTES_PER_DAY - departure;
  if (kind === 'last' && ret !== null) minutes = ret;
  if (kind === 'single') {
    if (departure !== null && ret !== null) minutes = Math.max(0, ret - departure);
    else if (departure !== null) minutes = MINUTES_PER_DAY - departure;
    else if (ret !== null) minutes = ret;
  }
  return minutes === null ? null : new Decimal(minutes).div(60);
}

export function computePerDiem(input: PerDiemInput): PerDiemResult {
  // Defence in depth: the request schemas already cap the trip length, but this is a pure function
  // and must not loop over millions of days if a caller ever skips them.
  if (diffDays(input.startDate, input.endDate) + 1 > MAX_TRAVEL_TRIP_DAYS) {
    throw new ValidationError(`A trip can be at most ${MAX_TRAVEL_TRIP_DAYS} days long`);
  }
  const personal = new Set(input.personalDays ?? []);
  const meals = new Map((input.mealsProvided ?? []).map((m) => [m.date, m]));
  const departure = clockMinutes(input.departureTime);
  const ret = clockMinutes(input.returnTime);
  const halfDayBelow =
    input.rule.halfDayBelowHours === null ? null : new Decimal(input.rule.halfDayBelowHours);
  const firstPct = new Decimal(input.rule.firstDayPct);
  const lastPct = new Decimal(input.rule.lastDayPct);

  const days: PerDiemDayLine[] = [];
  const excludedDates: string[] = [];
  let allowanceTotal = new Decimal(0);
  let mealTotal = new Decimal(0);
  let lodgingTotal = new Decimal(0);

  for (const date of eachDay(input.startDate, input.endDate)) {
    if (personal.has(date)) {
      excludedDates.push(date);
      continue;
    }

    const tonight = stayForNight(input.stays, date);
    const lastNight = stayForNight(input.stays, addDays(date, -1));
    const cityTier: TravelCityTier =
      tonight?.cityTier ?? lastNight?.cityTier ?? input.dayTripCityTier ?? 'tier_3';

    const dayKind: PerDiemDayKind =
      input.startDate === input.endDate
        ? 'single'
        : date === input.startDate
          ? 'first'
          : date === input.endDate
            ? 'last'
            : 'middle';

    let pct =
      dayKind === 'single'
        ? Decimal.min(firstPct, lastPct)
        : dayKind === 'first'
          ? firstPct
          : dayKind === 'last'
            ? lastPct
            : HUNDRED;
    let halfDay = false;
    if (dayKind !== 'middle' && halfDayBelow !== null) {
      const hours = hoursAway(dayKind, departure, ret);
      if (hours !== null && hours.lt(halfDayBelow)) {
        halfDay = true;
        pct = Decimal.min(pct, HALF_DAY_PCT);
      }
    }

    const rateValue = input.perDiemRates[cityTier];
    const rateMissing = rateValue === undefined || rateValue === null;
    const rate = money(new Decimal(rateMissing ? 0 : rateValue));
    const allowance = money(rate.mul(pct).div(HUNDRED));

    const provided = meals.get(date);
    let mealPct = new Decimal(0);
    if (provided?.breakfast) mealPct = mealPct.add(input.rule.breakfastPct);
    if (provided?.lunch) mealPct = mealPct.add(input.rule.lunchPct);
    if (provided?.dinner) mealPct = mealPct.add(input.rule.dinnerPct);
    const mealDeduction = Decimal.min(allowance, money(rate.mul(mealPct).div(HUNDRED)));

    let lodgingInLieu = new Decimal(0);
    if (tonight?.lodgingInLieu) {
      const lodgingRate = input.lodgingInLieuRates?.[tonight.cityTier];
      if (lodgingRate !== undefined && lodgingRate !== null) lodgingInLieu = money(new Decimal(lodgingRate));
    }

    const amount = allowance.sub(mealDeduction).add(lodgingInLieu);
    allowanceTotal = allowanceTotal.add(allowance);
    mealTotal = mealTotal.add(mealDeduction);
    lodgingTotal = lodgingTotal.add(lodgingInLieu);

    days.push({
      date,
      cityTier,
      dayKind,
      halfDay,
      pct: pct.toFixed(),
      rate: rate.toFixed(MONEY_SCALE),
      rateMissing,
      allowance: allowance.toFixed(MONEY_SCALE),
      mealDeduction: mealDeduction.toFixed(MONEY_SCALE),
      lodgingInLieu: lodgingInLieu.toFixed(MONEY_SCALE),
      amount: amount.toFixed(MONEY_SCALE),
    });
  }

  return {
    currency: input.currency,
    days,
    excludedDates,
    allowanceTotal: allowanceTotal.toFixed(MONEY_SCALE),
    mealDeductionTotal: mealTotal.toFixed(MONEY_SCALE),
    lodgingInLieuTotal: lodgingTotal.toFixed(MONEY_SCALE),
    total: allowanceTotal.sub(mealTotal).add(lodgingTotal).toFixed(MONEY_SCALE),
  };
}
